import type { ReactElement } from 'react'
import type { FormAsyncValidateOrFn } from '@tanstack/react-form'
import type { AlertVariant } from '@/components/ui/alert'
import type { GeocodedAddress, PlaceDetails } from '@/hooks/use-places-autocomplete'
import type { AddressVerificationState as AddressVerificationStateValue } from '@/lib/services/addresses/addresses.types'
import type { Location } from '@/lib/services/locations/locations.types'
import type { SalesOrder, SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { EmptyState } from '@/components/patterns/empty-state'
import { SelectableSurface } from '@/components/patterns/selectable-surface'
import { AddressVerificationDialog } from '@/components/forms/Address/AddressVerificationDialog'
import { OrderAsideFormFooter } from '@/components/orders/OrderAside'
import { OrderDetailPanel } from '@/components/orders/OrderDetailPanel'
import { isExternalFinancialSalesOrder } from '@/components/orders/sales-order-presentation'
import {
  ORDER_ASIDE_FORM_SURFACE_CLASS,
  ORDER_ASIDE_SUBTLE_SURFACE_CLASS,
} from '@/components/orders/order-aside.styles'
import { AddressFormatter } from '@/components/ui/address-formatter'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { usePlacesAutocomplete } from '@/hooks/use-places-autocomplete'
import { AddressVerificationState } from '@/lib/enums/AddressVerificationState'
import { Country } from '@/lib/enums/Country'
import { getAddressRegionOptions } from '@/lib/address-region-options'
import dayjs from '@/lib/dayjs'
import { DeliveryServiceabilityStatus } from '@/lib/enums/DeliveryServiceabilityStatus'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { ICONS } from '@/lib/icons'
import { serverFieldErrors, useAppForm } from '@/lib/form'
import { buildOrderDeliveryAddressFieldsFromPlace } from '@/lib/order-address-limits'
import { ORDER_FIELD_MAX_LENGTHS, ORDER_FIELD_NATIVE_MAX_LENGTHS } from '@/lib/order-field-limits'
import { locationFilters, locationQueries } from '@/lib/services/locations/locations.query'
import { getLocationName } from '@/lib/services/locations/locations.utils'
import {
  getFulfillmentDateAvailabilityWarning,
  getPosFulfillmentDateAvailability,
} from '@/lib/pos/fulfillment-availability'
import { useFsnNetworkIntegration } from '@/lib/services/fsn-network/fsn-network.query'
import { canSendOrderThroughFsnNetwork } from '@/lib/services/fsn-network/fsn-network.utils'
import { useUpdateSalesOrderMutation } from '@/lib/services/sales-orders/sales-orders.query'
import { cn, getErrorMessage, toYmdFromDate } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  CircleQuestionMark,
  MapPinIcon,
  RotateCcw,
  ShoppingBasket,
  Truck,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  applySuggestedDeliveryAddress,
  buildDeliveryAddressSearchString,
  clearDeliveryAddressVerification,
  createComparableDeliveryAddress,
  createDeliveryAddressMeta,
  createEmptyDeliveryAddress,
  createManuallyVerifiedAddress,
  getDraftDeliveryAddressVerificationState,
  isSuggestedAddressUsable,
  resolveProjectedDeliveryDestinationName,
  suggestedAddressMatchesTypedCore,
} from './fulfillment-destination.helpers'

type DestinationValues = {
  fulfillment_type: typeof FulfillmentType.Pickup.Value | typeof FulfillmentType.Delivery.Value
  fulfillment_due_date: string
  location_id: number | ''
  delivery_address: ReturnType<typeof createEmptyDeliveryAddress>
}

// The date controls take the form as a prop, so the form comes from a hook they can name.
function useDestinationForm(options: {
  defaultValues: DestinationValues
  validators: { onSubmitAsync: FormAsyncValidateOrFn<DestinationValues> }
}) {
  return useAppForm(options)
}

type DestinationForm = ReturnType<typeof useDestinationForm>

const DESTINATION_OPTIONS = [
  {
    id: FulfillmentType.Delivery.Value,
    title: 'Delivery',
    icon: Truck,
    tone: 'border-status-info-border bg-status-info-background text-status-info-foreground',
    idleTone:
      'border-border bg-card text-foreground hover:border-status-info-border hover:bg-status-info-background',
  },
  {
    id: FulfillmentType.Pickup.Value,
    title: 'Pickup',
    icon: ShoppingBasket,
    tone: 'border-primary bg-accentSecondary text-interaction',
    idleTone:
      'border-border/70 bg-background text-foreground hover:border-primary hover:bg-accentSecondary dark:hover:border-primary dark:hover:bg-accentSecondary',
  },
]

function buildDestinationValidationErrors({
  fulfillmentType,
  fulfillmentDueDate,
  locationId,
  address,
}: {
  fulfillmentType: DestinationValues['fulfillment_type']
  fulfillmentDueDate: string
  locationId: DestinationValues['location_id']
  address: DestinationValues['delivery_address']
}): Record<string, string> {
  const errors: Record<string, string> = {}

  if (!fulfillmentDueDate) {
    errors.fulfillment_due_date = `Choose a ${fulfillmentType === FulfillmentType.Pickup.Value ? 'pickup' : 'delivery'} date before saving.`
  }

  if (fulfillmentType === FulfillmentType.Pickup.Value) {
    if (!locationId) {
      errors.location_id = 'Choose a pickup location before saving.'
    }

    return errors
  }

  if (!String(address?.line1 ?? '').trim()) {
    errors['delivery_address.line1'] = 'Enter the delivery address before saving.'
  }

  if (!String(address?.city ?? '').trim()) {
    errors['delivery_address.city'] = 'Enter the city before saving.'
  }

  if (!String(address?.state ?? '').trim()) {
    errors['delivery_address.state'] = 'Enter the state before saving.'
  }

  if (!String(address?.postal_code ?? '').trim()) {
    errors['delivery_address.postal_code'] = 'Enter the zip code before saving.'
  }

  return errors
}

function formatDateOnly(value: string | null | undefined): string {
  return value ? dayjs.utc(value).format('YYYY-MM-DD') : ''
}

function buildPickupLocationOptions(locations: Location[] = []): { id: number; name: string }[] {
  return locations.map((location) => ({
    id: location.id,
    name: getLocationName(location),
  }))
}

function findLocationById(
  locations: Location[] = [],
  locationId: DestinationValues['location_id']
): Location | undefined {
  return locations.find((location) => String(location.id) === String(locationId ?? ''))
}

function hasChangedValue(
  current: Record<string, unknown>,
  initial: Record<string, unknown>
): boolean {
  return Object.keys(current).some((key) => current[key] !== initial[key])
}

function getDeliveryVerificationGuidance(
  verificationStateValue: AddressVerificationStateValue | undefined
): { variant: AlertVariant; description: string } | null {
  if (verificationStateValue === AddressVerificationState.VerifiedApproximate.Value) {
    return {
      variant: 'warning',
      description:
        'We found this area, but could not confirm the exact building. Double-check with the customer before delivery.',
    }
  }

  if (verificationStateValue === AddressVerificationState.Unverified.Value) {
    return {
      variant: 'destructive',
      description:
        "We couldn't verify this address. Contact the customer before delivery. You won't be able to add this order to a delivery route until the address is verified.",
    }
  }

  return null
}

function EditFulfillmentDateControls({
  form,
  fulfillmentType,
  locationId,
  pickupLocations,
}: {
  form: DestinationForm
  fulfillmentType: DestinationValues['fulfillment_type']
  locationId: DestinationValues['location_id']
  pickupLocations: Location[]
}): ReactElement {
  const isPickup = fulfillmentType === FulfillmentType.Pickup.Value
  const selectedPickupLocation = useMemo(
    () => (isPickup ? findLocationById(pickupLocations, locationId) : null),
    [isPickup, locationId, pickupLocations]
  )
  // Build the 365-day availability model once; calendar day matchers should only read Sets.
  const pickupDateAvailability = useMemo(() => {
    if (!selectedPickupLocation) {
      return null
    }

    return getPosFulfillmentDateAvailability(selectedPickupLocation, { isPickup: true })
  }, [selectedPickupLocation])
  const minDate = pickupDateAvailability
    ? dayjs(pickupDateAvailability.min).toDate()
    : dayjs().toDate()
  const maxDate = pickupDateAvailability ? dayjs(pickupDateAvailability.max).toDate() : null

  return (
    <>
      <form.AppField name="fulfillment_due_date">
        {(field) => (
          <field.DatePicker
            label={isPickup ? 'Pickup Date' : 'Delivery Date'}
            minDate={minDate}
            maxDate={isPickup ? maxDate : null}
            disabled={(date) =>
              isPickup
                ? (pickupDateAvailability?.hardDisabledDates?.has(toYmdFromDate(date)) ?? false)
                : false
            }
          />
        )}
      </form.AppField>

      {isPickup ? (
        <form.Subscribe selector={(state) => state.values.fulfillment_due_date}>
          {(fulfillmentDueDate) => {
            const dateWarning = getFulfillmentDateAvailabilityWarning(
              pickupDateAvailability,
              fulfillmentDueDate,
              { isPickup: true }
            )

            return dateWarning ? (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{dateWarning.title}</AlertTitle>
                <AlertDescription>{dateWarning.description}</AlertDescription>
              </Alert>
            ) : null
          }}
        </form.Subscribe>
      ) : null}
    </>
  )
}

function EditFulfillmentDestinationForm({
  order,
  closeAside,
  salesOrderId,
}: {
  order: SalesOrder
  closeAside?: () => void
  salesOrderId: SalesOrderIdInput
}): ReactElement {
  const { mutateAsync: updateSalesOrder, isPending } = useUpdateSalesOrderMutation()
  const { data: fsnIntegration = {} } = useFsnNetworkIntegration({
    organizationId: order?.organization_id,
  })
  const { geocodeAddress, geocodePlaceId } = usePlacesAutocomplete()
  const [verifyPrompt, setVerifyPrompt] = useState<{
    typedAddress: DestinationValues['delivery_address']
    suggested: GeocodedAddress
  } | null>(null)
  const verificationPromptChoiceRef = useRef<'typed' | 'suggested' | null>(null)
  const canUseFsnNetwork = canSendOrderThroughFsnNetwork(order, fsnIntegration)
  // A wire-in keeps the delivery type and location it was accepted with; only its details change here.
  const isWireIn = isExternalFinancialSalesOrder(order)
  const isSubmittedToFsnNetwork =
    order?.order_fulfillment?.type === 'fsn_network' &&
    Boolean(order?.order_fulfillment?.external_fulfillment_id)
  // Pickup dates read each location's calendar, which comes only with its availability changes.
  const pickupFilters = useMemo(
    () =>
      locationFilters()
        .defaultsWith({ include: ['availabilityChanges'] })
        .applyFilter({ active: true })
        .get(),
    []
  )
  const { data: activeLocations = [] } = useQuery({
    ...locationQueries.index({
      organizationId: order?.organization_id,
      filters: pickupFilters,
    }),
    placeholderData: [],
  })

  const pickupLocations = useMemo(
    () => activeLocations.filter((location) => Boolean(location?.is_pickup)),
    [activeLocations]
  )
  const defaultPickupLocationId = order?.location?.id ?? pickupLocations[0]?.id ?? ''
  const initialDeliveryAddressSource =
    order?.fulfillment_type === FulfillmentType.Delivery.Value ? order?.delivery_address : null
  const initialDeliveryDestinationName = resolveProjectedDeliveryDestinationName(order)
  const initialDeliveryAddress = useMemo(
    () =>
      createEmptyDeliveryAddress({
        ...initialDeliveryAddressSource,
        name: initialDeliveryDestinationName,
      }),
    [initialDeliveryAddressSource, initialDeliveryDestinationName]
  )
  const initialComparableAddress = useMemo(
    () => createComparableDeliveryAddress(initialDeliveryAddress),
    [initialDeliveryAddress]
  )
  const initialAddressMeta = useMemo(
    () => createDeliveryAddressMeta(initialDeliveryAddress),
    [initialDeliveryAddress]
  )
  const resetInactiveDestinationFields = (
    nextFulfillmentType: DestinationValues['fulfillment_type']
  ) => {
    form.setFieldValue('location_id', defaultPickupLocationId)
    form.setFieldValue('delivery_address', initialDeliveryAddress)
    form.setFieldValue('fulfillment_type', nextFulfillmentType)
  }

  const resetDeliveryVerification = () => {
    const currentAddress = form.store.state.values.delivery_address
    if (!currentAddress) {
      return
    }

    const alreadyCleared =
      currentAddress.google_place_id == null &&
      currentAddress.lat == null &&
      currentAddress.lng == null &&
      currentAddress.is_verified === false &&
      currentAddress.is_rooftop === false &&
      currentAddress.verification_state === AddressVerificationState.Unverified.Value

    const addressWithoutDestination = {
      ...currentAddress,
      name: '',
      postal_code_suffix: null,
    }

    form.setFieldValue(
      'delivery_address',
      alreadyCleared
        ? addressWithoutDestination
        : clearDeliveryAddressVerification(addressWithoutDestination)
    )
  }

  const applyDeliveryVerificationFields = (nextAddress: DestinationValues['delivery_address']) => {
    form.setFieldValue(
      'delivery_address.postal_code_suffix',
      nextAddress.postal_code_suffix ?? null
    )
    form.setFieldValue('delivery_address.google_place_id', nextAddress.google_place_id)
    form.setFieldValue('delivery_address.lat', nextAddress.lat)
    form.setFieldValue('delivery_address.lng', nextAddress.lng)
    form.setFieldValue('delivery_address.is_verified', nextAddress.is_verified)
    form.setFieldValue('delivery_address.is_rooftop', nextAddress.is_rooftop)
    form.setFieldValue('delivery_address.verification_state', nextAddress.verification_state)
  }

  const form = useDestinationForm({
    defaultValues: {
      fulfillment_type:
        order?.fulfillment_type === FulfillmentType.Pickup.Value
          ? FulfillmentType.Pickup.Value
          : FulfillmentType.Delivery.Value,
      fulfillment_due_date: formatDateOnly(order?.fulfillment_due_date),
      location_id: defaultPickupLocationId,
      delivery_address: initialDeliveryAddress,
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        const verificationPromptChoice = verificationPromptChoiceRef.current
        verificationPromptChoiceRef.current = null

        const errors = buildDestinationValidationErrors({
          fulfillmentType: value.fulfillment_type,
          fulfillmentDueDate: value.fulfillment_due_date,
          locationId: value.location_id,
          address: value.delivery_address,
        })

        if (Object.keys(errors).length > 0) {
          return { fields: errors }
        }

        try {
          const payload: Parameters<typeof updateSalesOrder>[0] = {
            id: salesOrderId,
            fulfillment_type: value.fulfillment_type,
            fulfillment_due_date: formatDateOnly(value.fulfillment_due_date),
          }

          if (value.fulfillment_type === FulfillmentType.Pickup.Value) {
            payload.location_id = value.location_id
          } else {
            let deliveryAddress = { ...value.delivery_address }

            const currentComparable = createComparableDeliveryAddress(deliveryAddress)
            const didCoreChangeSinceOpen = hasChangedValue(
              currentComparable,
              initialComparableAddress
            )
            const currentAddressMeta = createDeliveryAddressMeta(deliveryAddress)
            const didMetaChangeSinceOpen = hasChangedValue(currentAddressMeta, initialAddressMeta)

            if (
              didCoreChangeSinceOpen &&
              !didMetaChangeSinceOpen &&
              verificationPromptChoice !== 'typed'
            ) {
              deliveryAddress = clearDeliveryAddressVerification(deliveryAddress)
            }

            const hasCoords = deliveryAddress.lat != null && deliveryAddress.lng != null
            const hasPlaceId = Boolean(deliveryAddress.google_place_id)
            const isManuallyVerified =
              deliveryAddress.is_verified === true &&
              deliveryAddress.verification_state ===
                AddressVerificationState.ManuallyVerified.Value &&
              !hasCoords &&
              !hasPlaceId

            if (isManuallyVerified) {
              deliveryAddress = createManuallyVerifiedAddress(deliveryAddress)
            } else if (!hasCoords || !hasPlaceId) {
              const typedString = buildDeliveryAddressSearchString(deliveryAddress)
              const suggested = await geocodeAddress(typedString)

              if (!suggested || !isSuggestedAddressUsable(suggested)) {
                toast.error(
                  'Unable to find a reasonable address match. This address will be marked as manually verified.'
                )
                deliveryAddress = createManuallyVerifiedAddress(deliveryAddress)
              } else if (suggestedAddressMatchesTypedCore(deliveryAddress, suggested)) {
                deliveryAddress = applySuggestedDeliveryAddress(deliveryAddress, suggested)
              } else {
                setVerifyPrompt({
                  typedAddress: deliveryAddress,
                  suggested,
                })

                return
              }
            } else {
              deliveryAddress = {
                ...deliveryAddress,
                is_verified: true,
                is_rooftop: Boolean(deliveryAddress.is_rooftop),
                verification_state: deliveryAddress.is_rooftop
                  ? AddressVerificationState.Verified.Value
                  : AddressVerificationState.VerifiedApproximate.Value,
              }
            }

            const country = deliveryAddress.country || Country.UnitedStates.Value
            if (country !== Country.UnitedStates.Value && country !== Country.Canada.Value) {
              toast.error('We can only deliver to addresses in the United States or Canada.')

              return
            }

            payload.delivery_address = { ...deliveryAddress, country }
          }

          await updateSalesOrder(payload)
          toast.success('Destination updated successfully')
          closeAside?.()
          return undefined
        } catch (error) {
          toast.error('Destination update failed', {
            description: getErrorMessage(error),
          })

          return serverFieldErrors(error)
        }
      },
    },
  })

  useEffect(() => {
    const onlyPickupLocation = pickupLocations.length === 1 ? pickupLocations[0] : undefined
    if (!onlyPickupLocation) {
      return
    }

    const currentLocationId = form.store.state.values.location_id
    if (String(currentLocationId ?? '') === String(onlyPickupLocation.id)) {
      return
    }

    form.setFieldValue('location_id', onlyPickupLocation.id)
  }, [form, pickupLocations])

  const handlePlaceSelected = async (place: PlaceDetails) => {
    if (!place) {
      return
    }

    const selectedAddressFields = buildOrderDeliveryAddressFieldsFromPlace(place)

    form.setFieldValue('delivery_address.name', selectedAddressFields.name)
    form.setFieldValue('delivery_address.line1', selectedAddressFields.line1)
    form.setFieldValue('delivery_address.line2', selectedAddressFields.line2)
    form.setFieldValue('delivery_address.city', selectedAddressFields.city)
    form.setFieldValue('delivery_address.state', selectedAddressFields.state)
    form.setFieldValue('delivery_address.postal_code', selectedAddressFields.postal_code)
    form.setFieldValue(
      'delivery_address.postal_code_suffix',
      selectedAddressFields.postal_code_suffix
    )
    form.setFieldValue('delivery_address.country', place.country ?? '')

    if (place.lat !== undefined) {
      form.setFieldValue('delivery_address.lat', place.lat)
    }
    if (place.lng !== undefined) {
      form.setFieldValue('delivery_address.lng', place.lng)
    }

    if (place.placeId) {
      form.setFieldValue('delivery_address.google_place_id', place.placeId)
      form.setFieldValue('delivery_address.is_verified', true)

      const geo = await geocodePlaceId(place.placeId)
      if (form.getFieldValue('delivery_address.google_place_id') !== place.placeId) return
      if (
        !selectedAddressFields.postal_code_suffix &&
        geo?.postal_code === selectedAddressFields.postal_code &&
        geo?.postal_code_suffix
      ) {
        form.setFieldValue('delivery_address.postal_code_suffix', geo.postal_code_suffix)
      }
      const isRooftop = geo?.locationType === 'ROOFTOP' && geo?.partialMatch !== true
      form.setFieldValue('delivery_address.is_rooftop', Boolean(isRooftop))
      form.setFieldValue(
        'delivery_address.verification_state',
        isRooftop
          ? AddressVerificationState.Verified.Value
          : AddressVerificationState.VerifiedApproximate.Value
      )
    }
  }

  const handleUseTypedAddress = () => {
    const typedAddress = verifyPrompt?.typedAddress

    if (typedAddress) {
      verificationPromptChoiceRef.current = 'typed'
      form.setFieldValue('delivery_address', createManuallyVerifiedAddress(typedAddress))
      void Promise.resolve().then(() => form.handleSubmit())
    }

    setVerifyPrompt(null)
  }

  const handleUseSuggestedAddress = () => {
    const typedAddress = verifyPrompt?.typedAddress
    const suggested = verifyPrompt?.suggested

    if (typedAddress && isSuggestedAddressUsable(suggested)) {
      verificationPromptChoiceRef.current = 'suggested'
      form.setFieldValue('delivery_address', applySuggestedDeliveryAddress(typedAddress, suggested))
      void Promise.resolve().then(() => form.handleSubmit())
    }

    setVerifyPrompt(null)
  }

  return (
    <form.AppForm>
      <form.Form className="flex min-h-full flex-col pt-5 pl-0 sm:pt-6 sm:pl-1">
        <OrderDetailPanel
          title="Destination"
          subtitle={
            isWireIn
              ? 'This order came from another shop or site, so it stays a delivery. Update the date and address here.'
              : 'Choose whether this order should be delivered or picked up, then update the destination details.'
          }
          icon={MapPinIcon}
          tone="periwinkle"
          className="shadow-none"
        >
          <div className={ORDER_ASIDE_FORM_SURFACE_CLASS}>
            <div className="grid gap-y-5">
              <form.Subscribe
                selector={(state) => ({
                  fulfillment_type: state.values.fulfillment_type,
                  location_id: state.values.location_id,
                })}
              >
                {({ fulfillment_type: fulfillmentType, location_id: locationId }) => (
                  <>
                    {isWireIn ? null : (
                      <div className="grid gap-3 sm:grid-cols-2">
                        {DESTINATION_OPTIONS.map((option) => {
                          const Icon = option.icon
                          const isSelected = fulfillmentType === option.id

                          return (
                            <SelectableSurface
                              key={option.id}
                              type="button"
                              selected={isSelected}
                              radius="surface"
                              padding="none"
                              className={`px-4 py-3 text-left ${isSelected ? option.tone : option.idleTone}`}
                              onClick={() => {
                                if (fulfillmentType === option.id) {
                                  return
                                }

                                resetInactiveDestinationFields(option.id)
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <div className="mt-0.5 rounded-xl border border-current/15 bg-background/80 p-2 text-current">
                                  <Icon className="size-5" />
                                </div>
                                <div>
                                  <Typography variant="h5">{option.title}</Typography>
                                </div>
                              </div>
                            </SelectableSurface>
                          )
                        })}
                      </div>
                    )}

                    <div className="space-y-5">
                      <EditFulfillmentDateControls
                        form={form}
                        fulfillmentType={fulfillmentType}
                        locationId={locationId}
                        pickupLocations={pickupLocations}
                      />

                      {fulfillmentType === FulfillmentType.Pickup.Value ? (
                        <>
                          <div className="space-y-3 border-t border-border/60 pt-5">
                            <div>
                              <Typography variant="eyebrow">Pickup Location</Typography>
                            </div>

                            {pickupLocations.length > 1 ? (
                              <form.AppField name="location_id">
                                {(field) => (
                                  <field.SelectInput
                                    label="Location"
                                    options={buildPickupLocationOptions(pickupLocations)}
                                  />
                                )}
                              </form.AppField>
                            ) : (
                              <form.Subscribe selector={(state) => state.values.location_id}>
                                {(locationId) => {
                                  const selectedPickupLocation =
                                    pickupLocations[0] ??
                                    activeLocations.find(
                                      (location) => String(location.id) === String(locationId ?? '')
                                    ) ??
                                    order?.location

                                  return selectedPickupLocation ? (
                                    <div
                                      className={cn(ORDER_ASIDE_SUBTLE_SURFACE_CLASS, 'px-4 py-3')}
                                    >
                                      <Typography variant="h5">
                                        {getLocationName(selectedPickupLocation)}
                                      </Typography>
                                      {selectedPickupLocation?.address ? (
                                        <div className="mt-2 text-sm leading-6 text-muted-foreground">
                                          {/* The heading names the location; the address shows where it is. */}
                                          <AddressFormatter
                                            address={{
                                              ...selectedPickupLocation.address,
                                              name: null,
                                            }}
                                            variant="noCountry"
                                          />
                                        </div>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <EmptyState
                                      size="inline"
                                      surface="sunken"
                                      title="No pickup-capable location is currently available."
                                    />
                                  )
                                }}
                              </form.Subscribe>
                            )}
                          </div>
                        </>
                      ) : (
                        <form.Subscribe selector={(state) => state.values.delivery_address}>
                          {(deliveryAddress) => {
                            const verificationState =
                              getDraftDeliveryAddressVerificationState(deliveryAddress)
                            const VerificationIcon = verificationState
                              ? (ICONS[verificationState.Icon] ?? null)
                              : null
                            const showManualVerifyAction =
                              verificationState?.Value ===
                                AddressVerificationState.Unverified.Value ||
                              verificationState?.Value ===
                                AddressVerificationState.VerifiedApproximate.Value
                            const showRemoveManualVerifyAction =
                              verificationState?.Value ===
                              AddressVerificationState.ManuallyVerified.Value
                            const canMarkManuallyVerified = Boolean(
                              String(deliveryAddress?.line1 ?? '').trim() ||
                              String(deliveryAddress?.city ?? '').trim() ||
                              String(deliveryAddress?.state ?? '').trim() ||
                              String(deliveryAddress?.postal_code ?? '').trim()
                            )
                            const currentComparableAddress =
                              createComparableDeliveryAddress(deliveryAddress)
                            const verificationGuidance = getDeliveryVerificationGuidance(
                              verificationState?.Value
                            )
                            const canRestoreInitialVerification =
                              currentComparableAddress.line1 === initialComparableAddress.line1 &&
                              currentComparableAddress.city === initialComparableAddress.city &&
                              currentComparableAddress.state === initialComparableAddress.state &&
                              currentComparableAddress.postal_code ===
                                initialComparableAddress.postal_code &&
                              currentComparableAddress.country ===
                                initialComparableAddress.country &&
                              Boolean(initialAddressMeta.verification_state) &&
                              initialAddressMeta.verification_state !==
                                AddressVerificationState.ManuallyVerified.Value &&
                              initialAddressMeta.is_verified === true
                            const isOutsideDeliveryArea =
                              canUseFsnNetwork &&
                              order?.delivery_serviceability_status ===
                                DeliveryServiceabilityStatus.OutsideDeliveryArea.Value

                            return (
                              <div className="space-y-5 border-t border-border/60 pt-5">
                                {isOutsideDeliveryArea || isSubmittedToFsnNetwork ? (
                                  <Alert variant={isSubmittedToFsnNetwork ? 'warning' : 'info'}>
                                    <AlertTriangle className="h-4 w-4" />
                                    <AlertTitle>
                                      {isSubmittedToFsnNetwork
                                        ? 'Already sent to Flower Shop Network'
                                        : 'Outside your delivery area?'}
                                    </AlertTitle>
                                    <AlertDescription>
                                      {isSubmittedToFsnNetwork
                                        ? 'Saving delivery changes here will not update Flower Shop Network. Update Flower Shop Network separately if the delivery details changed.'
                                        : 'Save the address here, then use Out-of-area delivery on this order to send it to Flower Shop Network. Flower Shop Network will charge your account.'}
                                    </AlertDescription>
                                  </Alert>
                                ) : null}

                                <div className="grid gap-y-5">
                                  <form.AppField name="delivery_address.line1">
                                    {(field) => (
                                      <field.AddressSearch
                                        label="Address"
                                        placeholder="Start typing a business or address..."
                                        onChange={resetDeliveryVerification}
                                        onAddressSelected={handlePlaceSelected}
                                        maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.address_line1}
                                        characterMaxLength={ORDER_FIELD_MAX_LENGTHS.address_line1}
                                      />
                                    )}
                                  </form.AppField>

                                  <form.AppField name="delivery_address.name">
                                    {(field) => (
                                      <field.TextInput
                                        label="Business, funeral home, or church name (optional)"
                                        placeholder="For example, Rosewood Funeral Home"
                                        maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.address_name}
                                        characterMaxLength={ORDER_FIELD_MAX_LENGTHS.address_name}
                                      />
                                    )}
                                  </form.AppField>

                                  <form.AppField name="delivery_address.line2">
                                    {(field) => (
                                      <field.TextInput
                                        placeholder="Apartment, suite, unit, etc. (optional)"
                                        maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.address_line2}
                                        characterMaxLength={ORDER_FIELD_MAX_LENGTHS.address_line2}
                                      />
                                    )}
                                  </form.AppField>

                                  <form.AppField name="delivery_address.city">
                                    {(field) => (
                                      <field.TextInput
                                        label="City"
                                        placeholder="City"
                                        onChange={resetDeliveryVerification}
                                        maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.address_city}
                                        characterMaxLength={ORDER_FIELD_MAX_LENGTHS.address_city}
                                      />
                                    )}
                                  </form.AppField>

                                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <form.AppField name="delivery_address.state">
                                      {(field) => (
                                        <field.SelectInput
                                          label="State"
                                          placeholder="State"
                                          options={getAddressRegionOptions(field.state.value)}
                                          onValueChange={resetDeliveryVerification}
                                        />
                                      )}
                                    </form.AppField>

                                    <form.AppField name="delivery_address.postal_code">
                                      {(field) => (
                                        <field.TextInput
                                          label="Zip Code"
                                          placeholder="Zip Code"
                                          onChange={resetDeliveryVerification}
                                          maxLength={
                                            ORDER_FIELD_NATIVE_MAX_LENGTHS.address_postal_code
                                          }
                                          characterMaxLength={
                                            ORDER_FIELD_MAX_LENGTHS.address_postal_code
                                          }
                                        />
                                      )}
                                    </form.AppField>
                                  </div>
                                </div>

                                <div className="space-y-3 border-t border-border/60 pt-4">
                                  <div className="flex flex-wrap items-end justify-between gap-3">
                                    <div className="space-y-2">
                                      <Typography variant="eyebrow" className="block">
                                        Address Verification
                                      </Typography>
                                      {verificationState ? (
                                        <Badge
                                          variant={verificationState.Variant}
                                          size="control"
                                          shape="control"
                                        >
                                          {VerificationIcon ? (
                                            <VerificationIcon className="size-4" />
                                          ) : null}
                                          <span>{verificationState.Label}</span>
                                        </Badge>
                                      ) : (
                                        <Typography variant="supporting" className="block">
                                          Add or verify the delivery address before saving.
                                        </Typography>
                                      )}
                                    </div>

                                    {showManualVerifyAction ? (
                                      <ConfirmationDialog
                                        variant="question"
                                        title="Mark this address as manually verified?"
                                        description="Use this when you have confirmed the entered address is correct, even if Google could not verify the exact building."
                                        confirmText="Mark as Manually Verified"
                                        trigger={
                                          <Button
                                            type="button"
                                            variant="outline"
                                            disabled={!canMarkManuallyVerified}
                                          >
                                            <CircleQuestionMark className="size-4" />
                                            Mark as Manually Verified
                                          </Button>
                                        }
                                        onConfirm={() => {
                                          const nextAddress = createManuallyVerifiedAddress(
                                            form.store.state.values.delivery_address
                                          )
                                          applyDeliveryVerificationFields(nextAddress)
                                          toast.success(
                                            'Address marked as manually verified. Save to apply it to the order.'
                                          )
                                        }}
                                      />
                                    ) : null}

                                    {showRemoveManualVerifyAction ? (
                                      <ConfirmationDialog
                                        variant="question"
                                        title="Remove manual verification?"
                                        description="This will mark the address as unverified again so it can be reviewed before delivery."
                                        confirmText="Remove Manual Verification"
                                        trigger={
                                          <Button type="button" variant="outline">
                                            <RotateCcw className="size-4" />
                                            Remove Manual Verification
                                          </Button>
                                        }
                                        onConfirm={() => {
                                          const currentAddress =
                                            form.store.state.values.delivery_address
                                          const nextAddress = canRestoreInitialVerification
                                            ? {
                                                ...currentAddress,
                                                ...initialAddressMeta,
                                              }
                                            : clearDeliveryAddressVerification(currentAddress)
                                          applyDeliveryVerificationFields(nextAddress)
                                          toast.success(
                                            canRestoreInitialVerification
                                              ? 'Manual verification removed. Original verification status restored.'
                                              : 'Manual verification removed. Save to keep the address marked as unverified.'
                                          )
                                        }}
                                      />
                                    ) : null}
                                  </div>

                                  {verificationGuidance ? (
                                    <Alert variant={verificationGuidance.variant}>
                                      <AlertTriangle className="size-4" />
                                      <AlertDescription>
                                        {verificationGuidance.description}
                                      </AlertDescription>
                                    </Alert>
                                  ) : null}
                                </div>
                              </div>
                            )
                          }}
                        </form.Subscribe>
                      )}
                    </div>
                  </>
                )}
              </form.Subscribe>
            </div>
          </div>
        </OrderDetailPanel>
        <OrderAsideFormFooter
          form={form}
          mutation={{ isPending }}
          saveLabel="Save Destination"
          onCancel={closeAside}
        />
      </form.Form>

      <AddressVerificationDialog
        open={Boolean(verifyPrompt)}
        typedAddress={verifyPrompt?.typedAddress}
        suggested={verifyPrompt?.suggested}
        isSuggestedUsable={isSuggestedAddressUsable(verifyPrompt?.suggested)}
        onChooseTyped={handleUseTypedAddress}
        onChooseSuggested={handleUseSuggestedAddress}
        onCancel={() => setVerifyPrompt(null)}
      />
    </form.AppForm>
  )
}

export default EditFulfillmentDestinationForm
